/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { AnvilorFormsApiService } from "../../services/anvilor-forms-api.service";
import { AnvilorNotificationsService } from "@anvilor/ngx-apps-core";
import { repeat } from "rxjs";
import { retry } from "rxjs";
import { switchMap } from "rxjs";
import { catchError } from "rxjs";
import { timer } from "rxjs";
import { NETWORK_ISSUES_ERROR_MESSAGE } from "../../data/error-messages";

/** We hard-code (for now) this behavior to 6-minutes. */
const COFFEE_INTERVAL = 1000 * 60 * 6;

/**
 * The coffee util is a simple utility to ping the server upon startup.
 *
 * This serves 2 primary purposes:
 *
 *  1) Trigger the azure functions runtime to "warm-up" since we are currently
 *      deploying the server to an Azure function app. Depending on the functions
 *      runtime, this may or may not serve all that much benefit.
 *
 *  2) Determine when we are generally failing to reach the server, so we can
 *      warn/notify the user that things may be awry.
 */
export class AnvilorFormsAppCoffeeUtil {
  private formsApi: AnvilorFormsApiService;
  private notify: AnvilorNotificationsService;
  private store: AnvilorFormsAppStore;

  constructor(ctx: AnvilorFormsAppStoreContext) {
    this.formsApi = ctx.formsApi;
    this.notify = ctx.notify;
    this.store = ctx.store;
  }

  /**
   * Start sending "coffee" to the server (start pinging).
   *
   * We generally 'repeat' for the COFFEE_INTERVAL, but if we ever fail then
   * we wait 3 seconds and make another attempt. If that again fails we would
   * notify the user of the error, then resume making pings at the
   * COFFEE_INTERVAL indefinitely.
   */
  startCoffee(): void {
    const coffee$ = this.formsApi.postCoffee().pipe(
      repeat({ delay: COFFEE_INTERVAL }),
      retry({ delay: 3000, count: 1 }),
      catchError(() => {
        this.notifyCoffeeError();
        return timer(COFFEE_INTERVAL).pipe(
          switchMap(() => this.formsApi.postCoffee()),
          repeat({ delay: COFFEE_INTERVAL }),
          retry({ delay: COFFEE_INTERVAL })
        );
      })
    );

    this.store.subscribeTo(coffee$);
  }

  private notifyCoffeeError() {
    this.notify.toast.warning(NETWORK_ISSUES_ERROR_MESSAGE, "Network Issues!", {
      disableTimeOut: true,
    });
  }
}
